import type { ReactNode } from "react";
import { Outlet } from "react-router";

import { useIsPreviewOpen } from "@/modules/mine";
import { BookmarkImportProgressPanel } from "@/modules/mine/components/bookmark-import/bookmark-import-progress-panel";
import { usePrivacySessionStore } from "@/modules/mine/privacy/store";
import { usePreviewPlacement } from "@/modules/settings";
import { PluginToolbar } from "@/platform/plugins";
import { ImageLightboxProvider } from "@/shared/components/image-lightbox";
import { STORAGE_KEYS } from "@/shared/constants/storage-keys";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/shared/ui/resizable";
import { Separator } from "@/shared/ui/separator";
import { SidebarTrigger, SidebarInset } from "@/shared/ui/sidebar";
import Preview from "@/widgets/item-preview";

import { AddActionMenu } from "./add-action-menu";
import Search from "./search.tsx";
import { usePreviewPresentation } from "./use-preview-presentation";

interface WorkspaceShellProps {
  children?: ReactNode;
}

function PreviewSideOverlay() {
  return (
    <div className="fixed inset-0 z-[100] animate-in bg-black/10 duration-150 fade-in-0 motion-reduce:animate-none">
      <div className="absolute inset-y-0 right-0 w-[min(720px,calc(100vw-2rem))] animate-in overflow-hidden border-l bg-background shadow-2xl duration-200 slide-in-from-right motion-reduce:animate-none">
        <Preview />
      </div>
    </div>
  );
}

function PreviewBottomDrawer() {
  return (
    <div className="fixed inset-0 z-[100] flex animate-fade-in flex-col justify-end bg-black/40 backdrop-blur-[8px]">
      <div className="absolute inset-0" />
      <div className="relative h-[96vh] w-full animate-slide-up overflow-hidden rounded-t-2xl border-t border-white/5 bg-background shadow-[0_-20px_60px_rgba(0,0,0,0.35)]">
        <Preview disableContentAnimation />
      </div>
    </div>
  );
}

const WorkspaceShellContent = ({ children }: WorkspaceShellProps) => {
  const isPreviewOpen = useIsPreviewOpen();
  const previewPlacement = usePreviewPlacement();
  const { containerRef, presentation } =
    usePreviewPresentation(previewPlacement);
  const isSplitPreviewOpen = isPreviewOpen && presentation === "split";

  return (
    <div ref={containerRef} className="flex min-h-0 flex-1 overflow-hidden">
      <ResizablePanelGroup
        key={isSplitPreviewOpen ? "split" : "single"}
        autoSaveId={isSplitPreviewOpen ? STORAGE_KEYS.itemPreviewLayout : undefined}
        direction="horizontal"
      >
        <ResizablePanel
          id="main-panel"
          order={1}
          defaultSize={isSplitPreviewOpen ? 58 : 100}
          minSize={isSplitPreviewOpen ? 40 : 30}
          maxSize={isSplitPreviewOpen ? 68 : 100}
        >
          <SidebarInset className="h-full min-h-0 overflow-hidden">
            <div data-workspace-main className="relative flex h-full min-h-0 flex-col bg-[#fafafa] dark:bg-zinc-950">
              <header
                data-slot="workspace-header"
                className="absolute inset-x-0 top-0 z-30 border-b border-zinc-100 bg-[#fafafa] backdrop-blur supports-[backdrop-filter]:bg-zinc-50/50 dark:border-zinc-800 dark:bg-zinc-950"
              >
                <div className="flex h-14 items-center gap-3 px-8">
                  <SidebarTrigger className="-ml-1" />
                  <Separator orientation="vertical" className="mr-2 h-4!" />
                  <Search />
                  <div className="ml-auto flex items-center gap-2">
                    <PluginToolbar />
                    <AddActionMenu />
                  </div>
                </div>
              </header>
              <main
                data-workspace-scroll-container
                className="min-h-0 flex-1 overflow-y-auto pt-14"
              >
                <div data-slot="workspace-content" className="mx-auto min-h-full px-8 py-2">
                  {children ?? <Outlet />}
                </div>
              </main>
            </div>
          </SidebarInset>
        </ResizablePanel>
        {isSplitPreviewOpen && (
          <>
            <ResizableHandle withHandle />
            <ResizablePanel
              id="preview-panel"
              order={2}
              defaultSize={42}
              minSize={32}
              maxSize={60}
            >
              <div className="h-full flex-1 overflow-hidden bg-background">
                <Preview />
              </div>
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>

      {isPreviewOpen && presentation === "side-overlay" ? (
        <PreviewSideOverlay />
      ) : null}
      {isPreviewOpen && presentation === "bottom" ? (
        <PreviewBottomDrawer />
      ) : null}

      <BookmarkImportProgressPanel />
    </div>
  );
};

const WorkspaceShell = (props: WorkspaceShellProps) => {
  const lockVersion = usePrivacySessionStore((state) => state.lockVersion);
  return <ImageLightboxProvider resetSignal={lockVersion}>
    <WorkspaceShellContent {...props} />
  </ImageLightboxProvider>;
};

export default WorkspaceShell;
